import { TripOfferBox } from "@/components/price/PriceOffers";
import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Search, Star, Clock, Users, MessageCircle, Loader2, Calendar, Repeat, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import RoutePoints from "@/components/price/RoutePoints";
import { PriceScooterIcon } from "@/components/price/PriceIllustrations";
import TripThreadsSheet, { TripChat, useTripThreads } from "@/components/price/TripThreadsSheet";

const fmtDate = (d) => {
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  } catch { return d; }
};

/** Écran plein : tous les trajets publiés par les covoitureurs. */
export default function PublishedTripsScreen({ onBack, onBooked }) {
  const [filters, setFilters] = useState({ from_q: "", to_q: "" });
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seats, setSeats] = useState(1);
  const [chatThread, setChatThread] = useState(null);
  const [threadsOpen, setThreadsOpen] = useState(false);
  const threads = useTripThreads("client");

  const load = useCallback(async (f, nSeats = 1) => {
    setLoading(true);
    try {
      const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), seats: nSeats };
      const r = await api.get("/price/trips/feed", { params });
      setTrips(r.data?.trips || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les trajets");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load({ from_q: "", to_q: "" }); }, [load]);

  const contact = async (trip) => {
    try {
      const r = await api.post(`/price/trips/${trip.id}/contact`);
      setChatThread(r.data.thread);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const book = async (trip) => {
    const twoWheels = trip.vehicle_type === "motorcycle" || trip.vehicle_type === "scooter";
    let hasHelmet;
    if (twoWheels) {
      if (seats > 1) { toast.error("Un 2 roues n'accepte qu'1 passager"); return; }
      hasHelmet = window.confirm("Trajet en 2 roues : as-tu ton propre casque ? (OK = oui, Annuler = non)");
    }
    try {
      const r = await api.post(`/price/trips/${trip.id}/book`, { seats, has_helmet: hasHelmet });
      toast.success(`Réservé ! ${r.data?.seat?.total_price_eur ?? ""}€ — en attente de validation`);
      onBooked?.();
      load(filters, seats);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la réservation"); }
  };

  const upd = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen street-dots" style={{ background: "hsl(15 100% 96%)" }} data-testid="published-trips-screen">
      <div className="mx-auto w-full max-w-md px-5" style={{ paddingTop: "max(20px, env(safe-area-inset-top))", paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center justify-between gap-2">
          <button onClick={onBack} className="street-press grid h-10 w-10 place-items-center rounded-full bg-white street-border-thin street-shadow-sm" data-testid="published-trips-back">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button onClick={() => setThreadsOpen(true)} className="street-press relative inline-flex h-10 items-center gap-1.5 rounded-full bg-white px-3 street-border-thin street-shadow-sm" data-testid="published-trips-messages">
            <MessageCircle className="h-4 w-4" />
            <span className="text-[12px] font-black uppercase tracking-widest">Messages</span>
            {threads.unread > 0 && (
              <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-black text-primary-foreground street-border-thin" data-testid="published-trips-unread">
                {threads.unread}
              </span>
            )}
          </button>
        </div>

        <h1 className="mt-5 font-[Outfit] text-[28px] font-black leading-tight">Trajets publiés</h1>
        <p className="mt-1 text-[13px] font-medium text-foreground/70">
          Tous les trajets à venir des covoitureurs. Écris-leur ou réserve directement.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); load(filters, seats); }}
          className="mt-4 space-y-2 rounded-3xl bg-white p-4 street-border street-shadow"
          data-testid="published-trips-filters"
        >
          <div className="grid grid-cols-2 gap-2">
            <input value={filters.from_q} onChange={upd("from_q")} placeholder="Départ (ville)" className="h-11 rounded-xl bg-secondary/40 px-3 text-[14px] font-bold outline-none street-border-thin" data-testid="published-trips-from" />
            <input value={filters.to_q} onChange={upd("to_q")} placeholder="Arrivée (ville)" className="h-11 rounded-xl bg-secondary/40 px-3 text-[14px] font-bold outline-none street-border-thin" data-testid="published-trips-to" />
          </div>
          <div className="flex gap-2">
            <select value={seats} aria-label="Places" onChange={(e) => { const n = Number(e.target.value); setSeats(n); load(filters, n); }} className="h-11 flex-1 rounded-xl bg-secondary/40 px-3 text-[14px] font-bold street-border-thin" data-testid="published-trips-seats">
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} place{n > 1 ? "s" : ""}</option>)}
            </select>
            <button type="submit" className="street-press grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground street-border-thin" data-testid="published-trips-search">
              <Search className="h-4 w-4" strokeWidth={2.75} />
            </button>
          </div>
        </form>

        <div className="mt-5 space-y-4" data-testid="published-trips-list">
          {loading && <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />}
          {!loading && trips.length === 0 && (
            <div className="rounded-3xl bg-white p-6 text-center text-[13px] font-medium text-foreground/60 street-border-thin" data-testid="published-trips-empty">
              Aucun trajet publié pour ces critères. Reviens plus tard ou lance une demande instantanée.
            </div>
          )}
          {!loading && trips.map((t) => (
            <FeedCard key={t.id} trip={t} seats={seats} onContact={() => contact(t)} onBook={() => book(t)} />
          ))}
        </div>
      </div>

      <TripChat thread={chatThread} role="client" onClose={() => { setChatThread(null); threads.reload(); }} />
      <TripThreadsSheet open={threadsOpen} onClose={() => setThreadsOpen(false)} role="client" threads={threads} />
    </div>
  );
}

function FeedCard({ trip, seats, onContact, onBook }) {
  const seatsOk = (trip.seats_available ?? 0) >= seats;
  const twoWheels = trip.vehicle_type === "motorcycle" || trip.vehicle_type === "scooter";
  return (
    <div className="rounded-3xl bg-white p-5 street-border street-shadow" data-testid={`feed-card-${trip.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {trip.driver_photo_url ? (
            <img src={trip.driver_photo_url} alt="" className="h-11 w-11 flex-none rounded-2xl object-cover street-border-thin" />
          ) : (
            <div className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-primary font-[Outfit] text-[14px] font-black text-primary-foreground street-border-thin">
              {(trip.driver_name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate font-[Outfit] text-[14px] font-black">{trip.driver_name || "Covoitureur"}</div>
            <div className="mt-0.5 flex items-center gap-1 text-[11px] font-bold text-foreground/60">
              <Star className="h-3 w-3 text-accent" fill="currentColor" />
              {trip.driver_rating_count > 0 ? `${Number(trip.driver_rating_avg).toFixed(1)} (${trip.driver_rating_count})` : "Nouveau"}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-[Outfit] text-[20px] font-black leading-none text-primary" data-testid={`feed-price-${trip.id}`}>
            {Number(trip.price_per_seat_eur).toFixed(2)}€
          </div>
        </div>
      </div>
      <RoutePoints from={trip.from_address} to={trip.to_address} size="sm" className="mt-4" />
      {trip.nearby_labels?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`feed-nearby-${trip.id}`}>
          {trip.nearby_labels.map((l) => (
            <span key={l} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800 street-border-thin">
              <MapPin className="h-3.5 w-3.5" />{l}
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] font-black">
        <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 street-border-thin"><Calendar className="h-3.5 w-3.5" />{fmtDate(trip.date)}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2.5 py-1 street-border-thin"><Clock className="h-3.5 w-3.5" />{trip.time}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2.5 py-1 street-border-thin"><Users className="h-3.5 w-3.5" />{trip.seats_available}/{trip.seats_total}</span>
        {trip.repeat_label && (
          <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-primary-foreground street-border-thin" data-testid={`feed-repeat-${trip.id}`}><Repeat className="h-3.5 w-3.5" />{trip.repeat_label}</span>
        )}
        {twoWheels && (
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-2.5 py-1 street-border-thin"><PriceScooterIcon size={14} />2 roues</span>
        )}
      </div>
      {trip.notes && (
        <div className="mt-3 rounded-xl bg-secondary/40 p-2 text-[12px] font-medium text-foreground/70 street-border-thin">{trip.notes}</div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button onClick={onContact} className="street-press flex items-center justify-center gap-1.5 rounded-2xl bg-white py-3 font-[Outfit] text-[14px] font-black street-border street-shadow-sm" data-testid={`feed-contact-${trip.id}`}>
          <MessageCircle className="h-4 w-4" />Contacter
        </button>
        <button onClick={onBook} disabled={!seatsOk} className="street-press rounded-2xl bg-primary py-3 font-[Outfit] text-[14px] font-black text-primary-foreground street-border street-shadow-sm disabled:opacity-50" data-testid={`feed-book-${trip.id}`}>
          {seatsOk ? `Réserver ${seats}` : (trip.seats_available > 0 ? "Pas assez de places" : "Complet")}
        </button>
      </div>
      {trip.negotiable && seatsOk && <TripOfferBox trip={trip} seats={seats} />}
    </div>
  );
}
